/**
 * Elarune Co. - capa de movimiento
 *
 * Porta las interacciones del prototipo a JS vanilla, sin librerias:
 *   1. Aparicion al scroll         IntersectionObserver al 12%, disparo unico
 *   2. Cabecera al hacer scroll    centinela de 1px arriba del documento
 *   3. Paso activo del ritual      IntersectionObserver al 40%
 *   4. Carrusel antes/despues      scrollBy de una tarjeta
 *
 * Todo respeta prefers-reduced-motion y funciona dentro del editor de temas,
 * donde las secciones se montan y desmontan sin recargar la pagina.
 */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DESIGN_MODE = Boolean(window.Shopify && window.Shopify.designMode);

  /* ======================================================================
     1. Aparicion al scroll
     ====================================================================== */

  var Reveal = {
    observer: null,
    queued: false,
    fallbackTimer: null,

    show: function (el) {
      el.classList.add('ela-is-in');
    },

    showAll: function () {
      document.querySelectorAll('[data-ela-reveal]:not(.ela-is-in)').forEach(Reveal.show);
    },

    init: function () {
      // En el editor de temas nada se oculta: el usuario debe ver
      // el resultado al instante al anadir o mover una seccion.
      if (REDUCED.matches || DESIGN_MODE || !('IntersectionObserver' in window)) {
        Reveal.showAll();
        return;
      }

      Reveal.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            Reveal.show(entry.target);
            Reveal.observer.unobserve(entry.target);
          });
        },
        { threshold: 0.12 }
      );

      // El primer barrido va sincrono a proposito: requestAnimationFrame
      // no se ejecuta en pestanas de fondo, y si la pagina se abre en una
      // pestana que aun no se ha visto, nada llegaria a observarse.
      Reveal.observeNew();

      // Nodos que llegan tarde: paginacion, filtros, carga diferida.
      if ('MutationObserver' in window) {
        new MutationObserver(Reveal.scan).observe(document.body, {
          childList: true,
          subtree: true,
        });
      }

      // Red de seguridad: nada puede quedarse invisible de forma permanente.
      Reveal.fallbackTimer = setTimeout(Reveal.showAll, 2600);
    },

    observeNew: function () {
      if (!Reveal.observer) return;

      document
        .querySelectorAll('[data-ela-reveal]:not([data-ela-observed])')
        .forEach(function (el) {
          el.setAttribute('data-ela-observed', '');
          Reveal.observer.observe(el);
        });
    },

    // Los barridos posteriores se agrupan en un frame: el MutationObserver
    // puede dispararse decenas de veces seguidas al paginar o filtrar.
    scan: function () {
      if (Reveal.queued || !Reveal.observer) return;
      Reveal.queued = true;

      requestAnimationFrame(function () {
        Reveal.queued = false;
        Reveal.observeNew();
      });
    },
  };

  /* ======================================================================
     2. Cabecera al hacer scroll
     Un centinela de 1px en lo alto del documento. Cuando sale de vista,
     la franja de envio colapsa y la cabecera se vuelve translucida.
     ====================================================================== */

  var StickyHeader = {
    sentinel: null,

    apply: function (stuck) {
      var header = document.querySelector('[data-ela-header]');
      var topbar = document.querySelector('[data-ela-topbar]');
      if (!header) return;

      header.toggleAttribute('data-ela-stuck', stuck);
      if (topbar) topbar.toggleAttribute('data-ela-hidden', stuck);
    },

    onScroll: function () {
      if (!StickyHeader.sentinel) return;
      StickyHeader.apply(StickyHeader.sentinel.getBoundingClientRect().top < -6);
    },

    init: function () {
      StickyHeader.sentinel = document.querySelector('[data-ela-top-sentinel]');
      if (!StickyHeader.sentinel) return;

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            StickyHeader.apply(!entries[0].isIntersecting);
          },
          { threshold: 0 }
        ).observe(StickyHeader.sentinel);
      }

      // El observer solo no basta: dentro del editor de temas y en los
      // contenedores con scroll propio hace falta escuchar en captura.
      var listener = { passive: true, capture: true };
      window.addEventListener('scroll', StickyHeader.onScroll, listener);
      document.addEventListener('scroll', StickyHeader.onScroll, listener);
      StickyHeader.onScroll();
    },
  };

  /* ======================================================================
     3. Paso activo del ritual
     ====================================================================== */

  var RitualSteps = {
    observer: null,

    setActive: function (index) {
      document.querySelectorAll('[data-ela-step-link]').forEach(function (link) {
        var isActive = link.getAttribute('data-ela-step-link') === String(index);
        link.toggleAttribute('data-ela-active', isActive);
      });
    },

    init: function () {
      var steps = document.querySelectorAll('[data-ela-step]');
      if (!steps.length || !('IntersectionObserver' in window)) return;

      RitualSteps.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              RitualSteps.setActive(entry.target.getAttribute('data-ela-step'));
            }
          });
        },
        { threshold: 0.4 }
      );

      steps.forEach(function (step) {
        RitualSteps.observer.observe(step);
      });
    },
  };

  /* ======================================================================
     4. Carrusel antes/despues
     Avanza una tarjeta por pulsacion y desactiva la flecha en los extremos.
     ====================================================================== */

  var Carousel = {
    step: function (track, direction) {
      var card = track.firstElementChild;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 20;
      var distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.86;

      track.scrollBy({
        left: direction * distance,
        behavior: REDUCED.matches ? 'auto' : 'smooth',
      });
    },

    syncArrows: function (root, track) {
      var prev = root.querySelector('[data-ela-carousel-prev]');
      var next = root.querySelector('[data-ela-carousel-next]');
      var max = track.scrollWidth - track.clientWidth;

      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max - 4;
    },

    init: function (scope) {
      (scope || document).querySelectorAll('[data-ela-carousel]').forEach(function (root) {
        if (root.hasAttribute('data-ela-carousel-ready')) return;
        root.setAttribute('data-ela-carousel-ready', '');

        var track = root.querySelector('[data-ela-carousel-track]');
        if (!track) return;

        root.querySelectorAll('[data-ela-carousel-prev]').forEach(function (button) {
          button.addEventListener('click', function () {
            Carousel.step(track, -1);
          });
        });

        root.querySelectorAll('[data-ela-carousel-next]').forEach(function (button) {
          button.addEventListener('click', function () {
            Carousel.step(track, 1);
          });
        });

        track.addEventListener('scroll', function () {
          Carousel.syncArrows(root, track);
        }, { passive: true });

        window.addEventListener('resize', function () {
          Carousel.syncArrows(root, track);
        }, { passive: true });

        Carousel.syncArrows(root, track);
      });
    },
  };

  /* ======================================================================
     Arranque
     ====================================================================== */

  function init() {
    Reveal.init();
    StickyHeader.init();
    RitualSteps.init();
    Carousel.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // El editor de temas remonta secciones sueltas sin recargar la pagina.
  document.addEventListener('shopify:section:load', function (event) {
    Reveal.showAll();
    StickyHeader.init();
    RitualSteps.init();
    Carousel.init(event.target);
  });

  // Si el usuario activa el ahorro de movimiento a mitad de sesion,
  // se muestra todo en lugar de dejar contenido a medio aparecer.
  if (typeof REDUCED.addEventListener === 'function') {
    REDUCED.addEventListener('change', function (event) {
      if (event.matches) Reveal.showAll();
    });
  }
})();
